import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { OutreachDraft } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";
import { SubscriptionContext } from "./Subscription";

export enum TabPage {
  favorite_drafts = "saved",
  bento_recommendations = "bento",
}

export type DraftsMap = {
  [key: string]: {
    drafts: OutreachDraft[];
    cursor: number[] | null;
    hitsLimit: boolean;
  };
};

interface AutogeneratedDraftsContextInterface {
  removeLoading: number;
  fetchDrafts: (
    tab: TabPage,
    collectionId?: number,
    bentoBrandIds?: number[],
    isRefreshing?: boolean,
  ) => Promise<boolean>;
  pollForNewDrafts: (
    tab: TabPage,
    collectionId?: number,
    bentoBrandIds?: number[],
    isRefreshing?: boolean,
  ) => void;
  requestMoreDrafts: (tab: TabPage) => Promise<void>;
  checkGeneratingMore: () => Promise<void>;
  isGeneratingMore: boolean;
  setRemoveLoading: Dispatch<SetStateAction<number>>;
  isPolling: { [key in TabPage]: boolean };
  setSavedBrandCollectionId: Dispatch<SetStateAction<number>>;
  savedBrandCollectionId: number;
  draftsMap: DraftsMap;
  setDraftsMap: Dispatch<SetStateAction<DraftsMap>>;
  frozenIds: number[];
  setFrozenIds: Dispatch<SetStateAction<number[]>>;
  fetchLoading: boolean;
  setFetchLoading: Dispatch<SetStateAction<boolean>>;
  getMapsKey: (
    tab: TabPage | string | undefined,
    collectionId: number | undefined,
  ) => string;
  allDraftBrands: number[];
  setAllDraftBrands: Dispatch<SetStateAction<number[]>>;
  fetchDraftBentoBrandIds: () => Promise<void>;
  autogeneratedDraftBrands: number[];
  setAutogeneratedDraftBrands: Dispatch<SetStateAction<number[]>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  removeLoading: -1,
  fetchDrafts: defaultContextMissingFunction,
  pollForNewDrafts: defaultContextMissingFunction,
  requestMoreDrafts: defaultContextMissingFunction,
  checkGeneratingMore: defaultContextMissingFunction,
  isGeneratingMore: false,
  setRemoveLoading: defaultContextMissingFunction,
  isPolling: {
    [TabPage.bento_recommendations]: false,
    [TabPage.favorite_drafts]: false,
  },
  savedBrandCollectionId: 0,
  setSavedBrandCollectionId: defaultContextMissingFunction,
  draftsMap: {},
  setDraftsMap: defaultContextMissingFunction,
  frozenIds: [],
  setFrozenIds: defaultContextMissingFunction,
  fetchLoading: false,
  setFetchLoading: defaultContextMissingFunction,
  getMapsKey: defaultContextMissingFunction,
  allDraftBrands: [],
  setAllDraftBrands: defaultContextMissingFunction,
  fetchDraftBentoBrandIds: defaultContextMissingFunction,
  autogeneratedDraftBrands: [],
  setAutogeneratedDraftBrands: defaultContextMissingFunction,
};

const AutogeneratedDraftsContext =
  createContext<AutogeneratedDraftsContextInterface>(defaultInterface);

interface AutogeneratedDraftsProviderProps {
  children: React.ReactNode;
}

const RESULTS_PER_PAGE = 10;
export const MINIMUM_DRAFTS = 10;

const AutogeneratedDraftsProvider = ({
  children,
}: AutogeneratedDraftsProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [draftsMap, setDraftsMap] = useState<DraftsMap>({});
  const [isPolling, setIsPolling] = useState<{ [key in TabPage]: boolean }>({
    [TabPage.bento_recommendations]: false,
    [TabPage.favorite_drafts]: false,
  });
  const [removeLoading, setRemoveLoading] = useState<number>(-1);
  const [savedBrandCollectionId, setSavedBrandCollectionId] =
    useState<number>(0);
  const [frozenIds, setFrozenIds] = useState<number[]>([]);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [allDraftBrands, setAllDraftBrands] = useState<number[]>([]);
  const [autogeneratedDraftBrands, setAutogeneratedDraftBrands] = useState<
    number[]
  >([]);

  const fetchDraftBentoBrandIds = async () => {
    if (!currentOrg) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-drafts/bento-brand-ids?return_autogenerated=true`,
      );
      const draftBrands = [];
      const autogeneratedBrands = [];
      if (res?.drafts?.length === 0) {
        return;
      }
      for (const draft of res?.drafts) {
        if (draft[1]) {
          autogeneratedBrands.push(draft[0]);
        } else {
          draftBrands.push(draft[0]);
        }
      }
      setAllDraftBrands(draftBrands);
      setAutogeneratedDraftBrands(autogeneratedBrands);
    } catch (error) {
      setAllDraftBrands([]);
      setAutogeneratedDraftBrands([]);
    }
  };

  const fetchDrafts = async (
    tab: TabPage,
    collectionId?: number,
    bentoBrandIds?: number[],
    isRefreshingForNewBrand?: boolean,
    silent?: boolean,
  ): Promise<boolean> => {
    if (!currentOrg) return false;

    if (!silent) setFetchLoading(true);
    try {
      let url = `/api/organization/${currentOrg?.id}/outreach-drafts/recommendations-page?per_page=${RESULTS_PER_PAGE}`;
      const cursor = getCursor(tab, collectionId);
      if (cursor) {
        url += `&cursor=${JSON.stringify(cursor)}`;
      }
      if (tab === TabPage.favorite_drafts) {
        url += `&is_from_saved_brand=${true}`;
        if (collectionId) {
          setSavedBrandCollectionId(collectionId);
          url += `&saved_brand_collection_id=${collectionId}`;
        }
      } else {
        url += `&is_autogenerated=${true}&is_from_saved_brand=${false}`;
      }
      const res = await fetcherAuth(getToken, url);

      if (res.outreachDrafts?.length > 0) {
        _updateCollection(tab, res.outreachDrafts, false, collectionId);
        // End polling after all drafts are created for user from SavedBrandCollection.
        if (bentoBrandIds && bentoBrandIds?.length > 0) {
          setFrozenIds([]);
          const ids = res.outreachDrafts?.map((x: OutreachDraft) =>
            Number(x.bentoBrandId),
          );
          return bentoBrandIds.every((id) => ids.includes(id));
        } else if (isRefreshingForNewBrand) {
          return res.outreachDrafts?.length >= MINIMUM_DRAFTS;
        }
        await refreshChatNumber();
        return true;
      } else {
        // While silently polling for "+ more" drafts, avoid touching state until
        // new drafts actually arrive — re-rendering the list mid-poll causes a
        // visible scroll jump.
        if (!silent) {
          _updateCollection(tab, [], true, collectionId);
        }
        return false;
      }
    } catch (error) {
      if (!silent) setFrozenIds([]);
      return false;
    } finally {
      if (!silent) setFetchLoading(false);
    }
  };

  let intervalId: any = null;
  let timeoutId: any = null;

  const clear = (intervalId: any, timeoutId: any, tab: TabPage) => {
    clearInterval(intervalId);
    clearTimeout(timeoutId);
    setIsPolling((prev) => ({ ...prev, [tab]: false }));
  };

  const pollForNewDrafts = async (
    tab: TabPage,
    collectionId?: number,
    bentoBrandIds?: number[],
    isRefreshingForNewBrand?: boolean,
  ) => {
    if (isPolling[tab]) return;
    setIsPolling((prev) => ({ ...prev, [tab]: true }));

    const foundResult = await fetchDrafts(
      tab,
      collectionId,
      bentoBrandIds,
      isRefreshingForNewBrand,
    );
    if (!foundResult) {
      intervalId = setInterval(async () => {
        // If result is found
        const foundResult = await fetchDrafts(
          tab,
          collectionId,
          bentoBrandIds,
          isRefreshingForNewBrand,
        );
        if (foundResult) {
          clear(intervalId, timeoutId, tab);
        }
      }, 5000);
      // Poll every 5 seconds for 1 minute. If no new drafts are found nothing will happen on FE.
      timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        setIsPolling((prev) => ({ ...prev, [tab]: false }));
      }, 60000);
    } else {
      await refreshChatNumber();
      setIsPolling((prev) => ({ ...prev, [tab]: false }));
    }
  };

  const refreshChatNumber = async () => {
    try {
      const { remainingChatMessages } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/remaining-chat-messages`,
        "GET",
      );
      setSubscription((s) => {
        if (s) {
          return {
            ...s,
            remainingChatMessages,
          };
        } else {
          return s;
        }
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const requestMoreDrafts = async (tab: TabPage) => {
    if (!currentOrg) return;
    setIsGeneratingMore(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-drafts/request-more`,
        "POST",
      );
      pollForMoreDrafts(tab);
    } catch (error) {
      setIsGeneratingMore(false);
      throw error;
    }
  };

  const pollForMoreDrafts = async (tab: TabPage) => {
    // Poll silently so the list isn't re-rendered (and scroll isn't disturbed)
    // until the new drafts arrive and get appended at the bottom. New drafts have
    // a later created_at, so fetching from the existing cursor picks them up.
    const found = await fetchDrafts(tab, undefined, undefined, undefined, true);
    if (found) {
      await refreshChatNumber();
      setIsGeneratingMore(false);
      return;
    }

    const moreIntervalId = setInterval(async () => {
      const found = await fetchDrafts(
        tab,
        undefined,
        undefined,
        undefined,
        true,
      );
      if (found) {
        clearInterval(moreIntervalId);
        clearTimeout(moreTimeoutId);
        await refreshChatNumber();
        setIsGeneratingMore(false);
      }
    }, 5000);
    const moreTimeoutId = setTimeout(() => {
      clearInterval(moreIntervalId);
      setIsGeneratingMore(false);
    }, 60000);
  };

  const checkGeneratingMore = async () => {
    if (!currentOrg) return;
    try {
      const { isGenerating } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-drafts/generating-status`,
      );
      if (isGenerating && !isGeneratingMore) {
        pollForMoreDrafts(TabPage.bento_recommendations);
        setIsGeneratingMore(true);
      }
    } catch (error) {
      // best-effort resume; ignore errors
    }
  };

  const getCursor = (
    tab: TabPage | string | undefined,
    collectionId?: number,
  ): number[] | null => {
    const key = getMapsKey(tab, collectionId);
    return draftsMap[key]?.cursor;
  };

  const getMapsKey = (
    tab: TabPage | string | undefined,
    collectionId: number | undefined,
  ) => {
    let key: string = tab || TabPage.bento_recommendations;
    if (tab === TabPage.favorite_drafts) {
      if (collectionId !== undefined) {
        key = `${tab}_${collectionId}`;
      } else {
        key = `${tab}_${savedBrandCollectionId}`;
      }
    }
    return key;
  };

  const _updateCollection = (
    tab: TabPage,
    drafts: OutreachDraft[],
    hitsLimit: boolean,
    collectionId?: number,
  ) => {
    const key = getMapsKey(tab, collectionId);

    setDraftsMap((prev) => {
      const copy = makeDeepCopy(prev);
      const lastDraft = drafts?.[drafts?.length - 1];
      if (!(key in copy)) {
        copy[key] = {
          hitsLimit,
          cursor: lastDraft ? [lastDraft?.createdAt, lastDraft?.id] : null,
          drafts,
        };
      } else {
        const outreachDrafts = _getDrafts(copy[key]["drafts"], drafts);
        const lastDraft = outreachDrafts?.[outreachDrafts?.length - 1];
        copy[key]["drafts"] = outreachDrafts;
        copy[key]["hitsLimit"] = hitsLimit;
        copy[key]["cursor"] = lastDraft
          ? [lastDraft?.createdAt, lastDraft?.id]
          : null;
      }
      return copy;
    });

    // update autogeneratedDrafts
    setAutogeneratedDraftBrands((prev) => {
      const newIds = drafts
        ?.filter((draft) => draft.isAutogenerated && !!draft.bentoBrandId)
        ?.map((draft) => draft.bentoBrandId);

      const prevSet = new Set(prev);
      const updated = [...prev];

      for (const id of newIds) {
        if (!prevSet.has(id)) {
          updated.push(id);
          prevSet.add(id);
        }
      }

      return updated;
    });
  };

  const _getDrafts = (
    currentDrafts: OutreachDraft[],
    newDrafts: OutreachDraft[],
  ) => {
    const ids = currentDrafts?.map((draft: OutreachDraft) =>
      Number(draft.bentoBrandId),
    );
    const filtered = newDrafts?.filter(
      (x) => !ids?.includes(Number(x.bentoBrandId)),
    );
    return [...currentDrafts, ...filtered];
  };

  return (
    <AutogeneratedDraftsContext.Provider
      value={{
        draftsMap,
        setDraftsMap,
        pollForNewDrafts,
        requestMoreDrafts,
        checkGeneratingMore,
        isGeneratingMore,
        fetchDrafts,
        removeLoading,
        setRemoveLoading,
        isPolling,
        savedBrandCollectionId,
        setSavedBrandCollectionId,
        frozenIds,
        setFrozenIds,
        fetchLoading,
        setFetchLoading,
        getMapsKey,
        allDraftBrands,
        setAllDraftBrands,
        fetchDraftBentoBrandIds,
        autogeneratedDraftBrands,
        setAutogeneratedDraftBrands,
      }}
    >
      {children}
    </AutogeneratedDraftsContext.Provider>
  );
};

export { AutogeneratedDraftsProvider, AutogeneratedDraftsContext };
